// Mini modal with picture separated by a vertical line
import React, { useState } from 'react';
import { TinyColor } from '@ctrl/tinycolor';
import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  type RestModalConfirmCancelModal,
  type RestModalForwards,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import { FunnelButton, FunnelInput } from '@bbkFunnel/components/v2';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import {
  ModalDesktopInnerSpacer,
  ModalMobileInnerSpacer,
  OfferTitleMobile,
  TitleTheme,
  useMediaQueries,
} from '../sections';
import HelpText from '../sections/help-text';

import './modal.scss';

type Props = {
  modalInfo: RestModalConfirmCancelModal;
  close: () => void;
  handleAction: (forwards: RestModalForwards, cancel: 'cancel') => void;
};
const TermsDiv = styled.div`
  font-size: 0.8rem;
`;
const Spacing = styled.div`
  padding-bottom: 1rem;
`;

export const ConfirmCancel: React.FC<Props> = (props) => {
  const {
    modalInfo: {
      requires: {
        info: { title, terms, cta, phrase, placeholder },
        forwards,
        forwards: { text },
      },
    },
    close,
    handleAction,
  } = props;
  const { mobileView } = useMediaQueries();
  const theme = useTheme() as EmotionTheme;
  const [deleteTyped, setDeleteTyped] = useState(false);
  const [deleteValue, setDeleteValue] = useState('');

  const equalButtonSize = theme.complianceEqualButtonSize;

  const deletePhrase =
    phrase === undefined ? /^DELETE$/gi : new RegExp(`^${phrase}$`, 'gi');

  const handleInput = (value: string) => {
    const testPhrase = value.trim().match(deletePhrase) !== null;
    setDeleteTyped(testPhrase);
    setDeleteValue(value);
  };

  const color = theme.modalTextColor || theme.textColor;

  const InnerContent = (
    <>
      <Spacing>
        <HelpText
          cta={cta ?? 'Please type %s in the box below to close your account.'}
          phrase={phrase ?? 'DELETE'}
        />
      </Spacing>
      <Spacing>
        <FunnelInput
          placeholder={placeholder || 'Start typing...'}
          name="delete"
          value={deleteValue}
          onChange={(e) => handleInput(e.target.value)}
          additionalStyles={{
            color,
            border: `2px solid ${new TinyColor(color)
              .setAlpha(0.4)
              .toHexString()}`,
          }}
        />
      </Spacing>
      <Spacing
        className={cs({
          'tw-px-[11px]': equalButtonSize && mobileView,
          'tw-flex tw-justify-center': equalButtonSize && !mobileView,
        })}
      >
        <FunnelButton
          disabled={!deleteTyped}
          specificStyles={{ width: '100%' }}
          onClick={() => {
            if (deleteTyped) {
              handleAction(forwards, 'cancel');
            }
          }}
        >
          {text}
        </FunnelButton>
      </Spacing>
      {terms && <TermsDiv>{terms}</TermsDiv>}
    </>
  );

  if (mobileView) {
    return (
      <ModalMobileInnerSpacer>
        <OfferTitleMobile title={<TitleTheme title={title} />} close={close} />
        {InnerContent}
      </ModalMobileInnerSpacer>
    );
  }

  return (
    <ModalDesktopInnerSpacer
      css={css`
        max-width: 600px;
      `}
      className={cs('confirm-cancel', 'desktop', 'inner-spacer')}
    >
      <OfferTitleMobile title={<TitleTheme title={title} />} close={close} />
      {InnerContent}
    </ModalDesktopInnerSpacer>
  );
};
